import { useCallback } from "react";

export type AnnouncementPoliteness = "polite" | "assertive";

type AnnouncementListener = (
  message: string,
  politeness: AnnouncementPoliteness,
) => void;

const listeners = new Set<AnnouncementListener>();

export const subscribeToAnnouncements = (
  listener: AnnouncementListener,
): (() => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

export const announce = (
  message: string,
  politeness: AnnouncementPoliteness = "polite",
): void => {
  listeners.forEach((listener) => listener(message, politeness));
};

/**
 * useAnnounce
 *
 * Returns a stable function for announcing dynamic status messages to the
 * mounted live announcer. Announcements are safe no-ops when no live region is
 * mounted.
 */
export const useAnnounce = (): ((
  message: string,
  politeness?: AnnouncementPoliteness,
) => void) => {
  return useCallback((message, politeness = "polite") => {
    announce(message, politeness);
  }, []);
};
